import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, Location } from 'react-router-dom';
import { useAuth } from '../../auth/AuthContext';
import { sanitizeAuthErrorMessage } from '../../auth/authErrorUtils';
import { useWhiteLabel } from '../../design-system/whitelabel';
import type { AuthContextValue } from '../../types/auth.types';
import './Login.css';

type LocationState = {
    from?: {
        pathname?: string;
    };
};

const Login: React.FC = () => {
    const navigate = useNavigate();
    const location = useLocation() as Location & { state?: unknown };

    const { login, loading, error, isAuthenticated } = useAuth() as AuthContextValue;
    const { config: whiteLabel } = useWhiteLabel();

    const [isLoggingIn, setIsLoggingIn] = useState<boolean>(false);
    const [initError, setInitError] = useState<Error | null>(null);

    const from =
        ((location.state as LocationState | undefined)?.from?.pathname) || '/dashboard';

    // Redirect if already authenticated
    useEffect(() => {
        if (isAuthenticated && !loading) {
            // Keep console for debugging auth flow
            console.log('User already authenticated, redirecting to dashboard');
            navigate(from, { replace: true });
        }
    }, [isAuthenticated, loading, navigate, from]);

    // Check for initialization errors (e.g., MSAL/Entra configuration)
    useEffect(() => {
        if (error && error.message && error.message.includes('initialization')) {
            setInitError(error);
        }
    }, [error]);

    const handleLogin = async (): Promise<void> => {
        try {
            setIsLoggingIn(true);
            await login();
            navigate(from, { replace: true });
        } catch (err) {
            // Error is handled by AuthContext; log for diagnostics only
            console.error('Login failed:', err);
        } finally {
            setIsLoggingIn(false);
        }
    };

    // Show initialization error if auth provider failed to initialize
    if (initError) {
        return (
            <div className="login-page">
                <div className="login-container">
                    <div className="login-init-error">
                        <h2 className="login-init-error__title">Configuration Error</h2>
                        <p className="login-init-error__description">
                            Authentication service failed to initialize. Please check your environment configuration.
                        </p>
                        <pre className="login-init-error__details">
                            {initError.message}
                        </pre>
                    </div>
                </div>
            </div>
        );
    }

    // Don't render login form if user is already authenticated; show spinner while redirecting
    if (isAuthenticated) {
        return (
            <div className="login-loading">
                <div className="login-loading__spinner" />
            </div>
        );
    }

    return (
        <div className="login-page">
            <div className="login-container">
                {/* Logo/Brand Section */}
                <div className="login-brand">
                    {whiteLabel.logo && (
                        <img
                            src={whiteLabel.logo}
                            alt={`${whiteLabel.institutionName} logo`}
                            className="login-brand__logo"
                        />
                    )}
                    <h1 className="login-brand__title">{whiteLabel.institutionName} Admin</h1>
                    <p className="login-brand__subtitle">Secure access for authorized personnel</p>
                </div>

                {/* Login Card */}
                <div className="login-card">
                    <div className="login-card__header">
                        <h2 className="login-card__title">Welcome back</h2>
                        <p className="login-card__description">
                            Sign in with your organizational account
                        </p>
                    </div>

                    {/* Error Message */}
                    {error && (
                        <div className="login-error">
                            <svg className="login-error__icon" viewBox="0 0 20 20" fill="currentColor">
                                <path
                                    fillRule="evenodd"
                                    d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                                    clipRule="evenodd"
                                />
                            </svg>
                            <p className="login-error__message">
                                {sanitizeAuthErrorMessage(error)}
                            </p>
                        </div>
                    )}

                    {/* Microsoft Sign In Button */}
                    <button
                        onClick={handleLogin}
                        disabled={loading || isLoggingIn}
                        className="login-button"
                    >
                        {loading || isLoggingIn ? (
                            <>
                                <svg
                                    className="login-button__spinner"
                                    xmlns="http://www.w3.org/2000/svg"
                                    fill="none"
                                    viewBox="0 0 24 24"
                                >
                                    <circle
                                        className="login-button__spinner-track"
                                        cx="12"
                                        cy="12"
                                        r="10"
                                        stroke="currentColor"
                                        strokeWidth="4"
                                    />
                                    <path
                                        className="login-button__spinner-progress"
                                        fill="currentColor"
                                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                                    />
                                </svg>
                                Signing in...
                            </>
                        ) : (
                            <>
                                {/* Microsoft Logo */}
                                <svg
                                    className="login-button__icon"
                                    viewBox="0 0 21 21"
                                    xmlns="http://www.w3.org/2000/svg"
                                >
                                    <rect x="1" y="1" width="9" height="9" fill="#f25022" />
                                    <rect x="1" y="11" width="9" height="9" fill="#00a4ef" />
                                    <rect x="11" y="1" width="9" height="9" fill="#7fba00" />
                                    <rect x="11" y="11" width="9" height="9" fill="#ffb900" />
                                </svg>
                                Sign in with Microsoft
                            </>
                        )}
                    </button>

                    {/* Security Notice */}
                    <div className="login-security">
                        <div className="login-security__badge">
                            <svg className="login-security__badge-icon" viewBox="0 0 20 20" fill="currentColor">
                                <path fillRule="evenodd" d="M2.166 4.999A11.954 11.954 0 0010 1.944 11.954 11.954 0 0017.834 5c.11.65.166 1.32.166 2.001 0 5.225-3.34 9.67-8 11.317C5.34 16.67 2 12.225 2 7c0-.682.057-1.35.166-2.001zm11.541 3.708a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                            </svg>
                            <span className="login-security__badge-text">Enterprise Security</span>
                        </div>
                        <p className="login-security__text">Protected by Microsoft Entra ID</p>
                        <p className="login-security__text">Multi-factor authentication enabled</p>
                    </div>
                </div>

                {/* Footer */}
                <div className="login-footer">
                    <p className="login-footer__text">
                        Having trouble signing in?{' '}
                        <a
                            href={whiteLabel.supportEmail ? `mailto:${whiteLabel.supportEmail}` : '/support'}
                            className="login-footer__link"
                        >
                            Contact IT Support
                        </a>
                    </p>
                </div>
            </div>
        </div>
    );
};

export default Login;
